import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import swal from '@sweetalert/with-react';

import { filterArrayOfObjects, useDebouncedValue, deleteData, sortArrayOfObjects, loadSpecificData } from '../../../js/Helpers';
import { saveDataLineEditor } from '../../../js/SaveFunctions';

import SortToggle from '../../SortToggle';
import SchoolDataFilter, { applySchoolDataFilters } from '../../SchoolDataFilter';
import UploadModal from '../../UploadModal';
import EditClassroomData from '../modals/edit_classroom';
import { NO_DEPARTMENT_FILTER_VALUE, prependNoDepartmentFilterOption } from '../utils/departmentFilterUtils';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch, faEdit, faPlus, faUpload, faMinusSquare, faSortDown, faCheckSquare } from '@fortawesome/free-solid-svg-icons';
import { faTrashAlt, faSquare as farSquare } from '@fortawesome/free-regular-svg-icons';

const normalizeDepartmentIDs = (department_ids = []) => {
	if(!Array.isArray(department_ids)) return [];

	return department_ids.reduce((results, department_id) => {
		const normalized_department_id = `${department_id || ''}`.trim();
		if(!normalized_department_id || normalized_department_id === '0' || normalized_department_id === 'null' || normalized_department_id === 'undefined') return results;
		if(results.includes(normalized_department_id)) return results;
		results.push(normalized_department_id);
		return results;
	}, []);
};

const buildClassroomDraft = (classroom_info = {}) => ({
	classroom_id: classroom_info.classroom_id,
	classroom_name: classroom_info.classroom_name || '',
	classroom_type_id: classroom_info.classroom_type_id ? `${classroom_info.classroom_type_id}` : '',
	departments: normalizeDepartmentIDs(classroom_info.departments),
});

const mergeSavedClassroomIntoList = (classroom_list = [], old_classroom_id, saved_classroom) => {
	const old_classroom_id_string = `${old_classroom_id}`;
	const new_classroom_id_string = `${saved_classroom.classroom_id}`;
	const original_classroom_index = classroom_list.findIndex((classroom_info) => `${classroom_info.classroom_id}` === old_classroom_id_string);

	if(old_classroom_id_string === new_classroom_id_string)
	{
		return classroom_list.map((classroom_info) => (
			`${classroom_info.classroom_id}` === old_classroom_id_string ? saved_classroom : classroom_info
		));
	}

	const next_classroom_list = classroom_list.filter((classroom_info) => (
		`${classroom_info.classroom_id}` !== old_classroom_id_string && `${classroom_info.classroom_id}` !== new_classroom_id_string
	));

	if(original_classroom_index === -1) return [...next_classroom_list, saved_classroom];

	const safe_insert_index = Math.min(original_classroom_index, next_classroom_list.length);
	return [
		...next_classroom_list.slice(0, safe_insert_index),
		saved_classroom,
		...next_classroom_list.slice(safe_insert_index),
	];
};

const classroomsMatch = (classroom_a = {}, classroom_b = {}) => {
	const normalized_a = buildClassroomDraft(classroom_a);
	const normalized_b = buildClassroomDraft(classroom_b);

	return normalized_a.classroom_name.trim() === normalized_b.classroom_name.trim()
		&& `${normalized_a.classroom_type_id}` === `${normalized_b.classroom_type_id}`
		&& JSON.stringify([...normalized_a.departments].sort()) === JSON.stringify([...normalized_b.departments].sort());
};

export default function DatabaseDataClassrooms({ schoolInfo, databaseData, setDatabaseData, scheduleStructureData, scheduleInfo, teachers, courses, sections, departments, dataIsLoading, handlePullNewData, readOnlyAddUpload = false, visibleDataFilter = null, departmentFilterOptions = departments, showDepartmentFilter = true}) {
	const [isLoading, setIsLoading] = useState(true);
	const [handleChangeComplete, setHandleChangeComplete] = useState(false);

	const [filteredResults, setFilteredResults] = useState([]);
	const [filterActive, setFilterActive] = useState(false);
	const [displayResults, setDisplayResults] = useState([]);
	const [isSearching, setIsSearching] = useState(false);
	const [searchValue, setSearchValue] = useState('');
	const debouncedSearchValue = useDebouncedValue(searchValue, 120);
	
	const [showEditDataModal, setShowEditDataModal] = useState(false);
	const [showUploadModal, setShowUploadModal] = useState(false);
	const [currentDataID, setCurrentDataID] = useState(null);
	
	const [selectedData, setSelectedData] = useState([]);
	const [dontShowDeletePopup, setDontShowDeletePopup] = useState(false);

	const [sortedBy, setSortedBy] = useState('classroom_name');
	const [sortAscDesc, setSortAscDesc] = useState('asc');

	const [editingCell, setEditingCell] = useState(null);
	const [cellDraft, setCellDraft] = useState(null);
	const [isSavingCell, setIsSavingCell] = useState(false);

	const filteredResultsRef = useRef(filteredResults);
	const activeEditorRef = useRef(null);
	const classroomNameInputRef = useRef(null);
	filteredResultsRef.current = filteredResults;
	const visibleDatabaseData = useMemo(() => (
		typeof visibleDataFilter === 'function' ? databaseData.filter(visibleDataFilter) : databaseData
	), [databaseData, visibleDataFilter]);
	const departmentFilterOptionsWithNoDepartment = useMemo(() => prependNoDepartmentFilterOption(departmentFilterOptions), [departmentFilterOptions]);
	const schoolDataFilterCategories = useMemo(() => {
		const categories = [];
		if(showDepartmentFilter)
		{
			categories.push({
				key: 'departments',
				title: 'Departments',
				dataKey: 'departments',
				noValueFilterValue: NO_DEPARTMENT_FILTER_VALUE,
				options: departmentFilterOptionsWithNoDepartment.map(department_info => ({
					value: department_info.department_id,
					display: department_info.department,
				})),
			});
		}
		return categories;
	}, [departmentFilterOptionsWithNoDepartment, showDepartmentFilter]);
	const getDepartmentSortText = useCallback((department_ids = []) => {
		const normalized_department_ids = normalizeDepartmentIDs(department_ids);
		if(normalized_department_ids.length === 0) return 'No department';

		return normalized_department_ids.map(department_id => {
			const department_index = departments.findIndex((department_info) => `${department_info.department_id}` === `${department_id}`);
			return department_index !== -1 ? departments[department_index].department : 'No department';
		}).sort((department_a, department_b) => `${department_a}`.localeCompare(`${department_b}`, undefined, {numeric:true, sensitivity:'base'})).join(', ');
	}, [departments]);
	const sortByDepartmentDisplay = useCallback((classroom_list = [], sort_asc_desc = 'asc') => (
		[...(Array.isArray(classroom_list) ? classroom_list : [])].sort((classroom_a, classroom_b) => {
			const comparison = getDepartmentSortText(classroom_a.departments).localeCompare(getDepartmentSortText(classroom_b.departments), undefined, {numeric:true, sensitivity:'base'});
			return sort_asc_desc === 'asc' ? comparison : comparison * -1;
		})
	), [getDepartmentSortText]);

	const applyCurrentSort = useCallback((classroom_list = []) => (
		sortedBy === 'departments'
			? sortByDepartmentDisplay(classroom_list, sortAscDesc)
			: sortArrayOfObjects(Array.isArray(classroom_list) ? classroom_list : [], sortedBy, null, sortAscDesc)
	), [sortByDepartmentDisplay, sortedBy, sortAscDesc]);
	
	const handleIsSearching = (e) => {
		if(e.keyCode !== 8)
		{
			setIsLoading(false);
			setIsSearching(true);
		}
		else
		{
			setIsLoading(true);
			setIsSearching(false);
		}
	};
	
	const handleChange = useCallback(() => {
		if(filteredResultsRef.current.length === 0 && dataIsLoading) return;
		
		const search_bar = document.getElementsByClassName("school-data-search-bar")[0];
		const search_value = search_bar ? search_bar.value : '';
		let next_display_results = [];
		
		if(search_value.length > 1)
		{
			next_display_results = filterArrayOfObjects(filteredResultsRef.current, search_value, ['classroom_name']);
		}
		else
		{
			next_display_results = filteredResultsRef.current;
		}
		next_display_results = applyCurrentSort(next_display_results);
		setDisplayResults([...next_display_results]);
		
		setIsLoading(false);
		setIsSearching(false);
		setHandleChangeComplete(true);
	}, [applyCurrentSort, dataIsLoading]);

	useEffect(() => {
		handleChange();
	}, [debouncedSearchValue, handleChange]);

	const isEditingField = useCallback((classroom_id, field) => (
		editingCell
		&& `${editingCell.classroom_id}` === `${classroom_id}`
		&& editingCell.field === field
	), [editingCell]);

	const cancelCellEdit = useCallback(() => {
		setEditingCell(null);
		setCellDraft(null);
		setIsSavingCell(false);
	}, []);

	const getClassroomByID = useCallback((classroom_id) => (
		databaseData.find((classroom_info) => `${classroom_info.classroom_id}` === `${classroom_id}`) || null
	), [databaseData]);

	const startCellEdit = useCallback((classroom_info, field) => {
		if(!classroom_info) return;

		setEditingCell({classroom_id: classroom_info.classroom_id, field});
		setCellDraft(buildClassroomDraft(classroom_info));
		setIsSavingCell(false);
	}, []);

	useEffect(() => {
		if(!editingCell || editingCell.field !== 'classroom_name') return;
		if(classroomNameInputRef.current)
		{
			classroomNameInputRef.current.focus();
			classroomNameInputRef.current.select();
		}
	}, [editingCell]);

	const classroomTypeOptions = Array.isArray(scheduleStructureData?.classroom_types) ? scheduleStructureData.classroom_types : [];

	const getDepartmentName = (department_id) => {
		const department_index = departments.findIndex((department_info) => `${department_info.department_id}` === `${department_id}`);
		return department_index !== -1 ? departments[department_index].department : 'No department';
	};

	const getClassroomTypeName = useCallback((classroom_type_id) => {
		const classroom_type_index = classroomTypeOptions.findIndex((classroom_type_info) => `${classroom_type_info.classroom_type_id}` === `${classroom_type_id}`);
		return classroom_type_index !== -1 ? classroomTypeOptions[classroom_type_index].classroom_type : '';
	}, [classroomTypeOptions]);

	const getClassroomTypeDisplay = useCallback((classroom_info = {}) => {
		return classroom_info.classroom_type || getClassroomTypeName(classroom_info.classroom_type_id) || 'None';
	}, [getClassroomTypeName]);

	const renderDepartmentDisplay = (department_ids = []) => {
		const normalized_department_ids = normalizeDepartmentIDs(department_ids);

		if(normalized_department_ids.length === 0)
		{
			return <div className='school-data-department medium-text'>No department</div>;
		}

		return normalized_department_ids.map((department_id, index) => (
			<div className='school-data-department medium-text' key={`${department_id}-${index}`}>{getDepartmentName(department_id)}</div>
		));
	};

	const updateDraftField = (field, value) => {
		setCellDraft((current_draft) => ({...current_draft, [field]: value}));
	};

	const toggleDepartmentSelection = (department_id) => {
		const normalized_department_id = `${department_id}`;

		setCellDraft((current_draft) => {
			if(!current_draft) return current_draft;

			const department_is_selected = current_draft.departments.includes(normalized_department_id);
			const next_departments = department_is_selected
				? current_draft.departments.filter((selected_department_id) => `${selected_department_id}` !== normalized_department_id)
				: [...current_draft.departments, normalized_department_id];

			return {...current_draft, departments: next_departments};
		});
	};

	const handleSaveCell = useCallback(async (classroom_info, draft_override = null) => {
		const draft_to_save = draft_override || cellDraft;
		if(!draft_to_save || !editingCell || isSavingCell) return;

		const trimmed_classroom_name = draft_to_save.classroom_name ? draft_to_save.classroom_name.trim() : '';
		const normalized_departments = normalizeDepartmentIDs(draft_to_save.departments);
		const next_classroom_type_id = draft_to_save.classroom_type_id ? `${draft_to_save.classroom_type_id}` : '';
		const draft_to_compare = {
			...classroom_info,
			classroom_name: trimmed_classroom_name,
			classroom_type_id: next_classroom_type_id,
			departments: normalized_departments,
		};

		if(classroomsMatch(classroom_info, draft_to_compare))
		{
			cancelCellEdit();
			return;
		}

		if(!trimmed_classroom_name)
		{
			swal({
				title: "Missing classroom name",
				icon:"warning",
				buttons: {
					cancel: {
						text: "Okay",
						value: false,
						visible: true,
						className: 'blue-btn'
					}
				},
				content: (
					<div>
						<p>You are missing a classroom name! Please add a name for this classroom before saving.</p>
					</div>
				)
			});
			return;
		}

		setIsSavingCell(true);
		if(draft_override) setCellDraft(draft_override);

		const original_classroom_id = classroom_info.classroom_id;
		const response_data = await saveDataLineEditor({
			school_id: schoolInfo.school_id,
			schedule_version_id: schoolInfo.current_schedule_version_id,
			data_type: 'classroom',
			data_to_save: {
				classroom_id: original_classroom_id,
				classroom_name: trimmed_classroom_name,
				classroom_type_id: next_classroom_type_id,
				departments: normalized_departments,
			}
		});

		if(!response_data?.new_data_id)
		{
			setIsSavingCell(false);
			swal({
				title: "Unable to Save",
				icon:"warning",
				buttons: {
					cancel: {
						text: "Okay",
						value: false,
						visible: true,
						className: 'blue-btn'
					}
				},
				content: (
					<div>
						<p>We couldn&rsquo;t save this classroom value. Please try again.</p>
					</div>
				)
			});
			return;
		}

		const saved_classroom_id = response_data.new_data_id;
		let saved_classroom = {
			...classroom_info,
			classroom_id: saved_classroom_id,
			classroom_name: trimmed_classroom_name,
			classroom_type_id: next_classroom_type_id,
			classroom_type: getClassroomTypeName(next_classroom_type_id),
			departments: normalized_departments,
		};

		const refreshed_classroom = await loadSpecificData({
			data_type:'classroom',
			data_id:saved_classroom_id,
			data_version_id:schoolInfo.current_data_version_id,
			schedule_version_id:schoolInfo.current_schedule_version_id,
			school_id:schoolInfo.school_id,
		});

		if(refreshed_classroom && !refreshed_classroom.msg)
		{
			if(`${refreshed_classroom.classroom_name || ''}` !== trimmed_classroom_name)
			{
				setIsSavingCell(false);
				swal({
					title: "Unable to Verify Save",
					icon:"warning",
					buttons: {cancel: {text: "Okay", value: false, visible: true, className: 'blue-btn'}},
					content: (<div><p>The saved classroom name did not match the casing you entered. Please try again.</p></div>)
				});
				return;
			}
			saved_classroom = {
				...saved_classroom,
				...refreshed_classroom,
				classroom_type: refreshed_classroom.classroom_type || getClassroomTypeName(refreshed_classroom.classroom_type_id || next_classroom_type_id),
			};
		}

		const next_database_data = mergeSavedClassroomIntoList(databaseData, original_classroom_id, saved_classroom);
		const next_filtered_results = mergeSavedClassroomIntoList(filteredResults, original_classroom_id, saved_classroom);

		setDatabaseData(next_database_data);
		setFilteredResults(next_filtered_results);
		setSelectedData((current_selected_data) => {
			if(`${saved_classroom_id}` === `${original_classroom_id}`) return current_selected_data;

			const next_selected_data = current_selected_data.map((selected_id) => (
				`${selected_id}` === `${original_classroom_id}` ? saved_classroom_id : selected_id
			));

			return [...new Set(next_selected_data)];
		});
		cancelCellEdit();
	}, [cellDraft, editingCell, isSavingCell, schoolInfo, databaseData, filteredResults, setDatabaseData, cancelCellEdit, getClassroomTypeName]);

	const handleCellBlur = (classroom_info, field) => {
		if(!isEditingField(classroom_info.classroom_id, field) || isSavingCell) return;

		window.setTimeout(() => {
			const active_element = document.activeElement;
			const focus_stayed_inside = activeEditorRef.current && active_element ? activeEditorRef.current.contains(active_element) : false;
			if(focus_stayed_inside) return;
			handleSaveCell(classroom_info);
		}, 0);
	};

	const handleCellKeyDown = async (event, classroom_info) => {
		if(event.key === 'Enter')
		{
			event.preventDefault();
			await handleSaveCell(classroom_info);
		}
		else if(event.key === 'Escape')
		{
			event.preventDefault();
			cancelCellEdit();
		}
	};

	const handleClassroomTypeSelect = (classroom_info, classroom_type_id) => {
		const next_draft = {...(cellDraft || buildClassroomDraft(classroom_info)), classroom_type_id: `${classroom_type_id}`};
		handleSaveCell(classroom_info, next_draft);
	};

	useEffect(() => {
		if(!editingCell || !['departments', 'classroom_type_id'].includes(editingCell.field)) return;

		const handleOutsideClick = (event) => {
			if(activeEditorRef.current && activeEditorRef.current.contains(event.target)) return;

			const classroom_info = getClassroomByID(editingCell.classroom_id);
			if(!classroom_info)
			{
				cancelCellEdit();
				return;
			}

			handleSaveCell(classroom_info);
		};

		document.addEventListener('mousedown', handleOutsideClick);
		return () => document.removeEventListener('mousedown', handleOutsideClick);
	}, [editingCell, handleSaveCell, getClassroomByID, cancelCellEdit]);

	const handleSortClick = (sortedData, sorted_by, sort_asc_desc) => {
		cancelCellEdit();
		setSortedBy(sorted_by);
		setSortAscDesc(sort_asc_desc);
		if(sorted_by === 'departments')
		{
			setDisplayResults(sortByDepartmentDisplay(displayResults, sort_asc_desc));
			return;
		}
		setDisplayResults([...sortedData]);
	};
	
	const handleFilterClick = (selectedFilters = {}) => {
		cancelCellEdit();
		const filtered_data = applySchoolDataFilters(visibleDatabaseData, selectedFilters, schoolDataFilterCategories);
		setFilterActive(Object.keys(selectedFilters).some(filter_key => (selectedFilters[filter_key] || []).length > 0));
		setFilteredResults([...filtered_data]);
	};
	
	const toggleEditData = (dataID) => {
		cancelCellEdit();
		if(showEditDataModal)
		{
			setShowEditDataModal(false);
			return;
		}

		setCurrentDataID(dataID);
		setShowEditDataModal(true);
	};
	
	const toggleDontShow = () => {
		setDontShowDeletePopup(!dontShowDeletePopup);
	};

	const toggleSelectData = (data_id) => {
		const data_selected_index = selectedData.findIndex((data) => `${data}` === `${data_id}`);

		if(data_selected_index !== -1)
		{
			selectedData.splice(data_selected_index, 1);
		}
		else
		{
			selectedData.push(data_id);
		}

		setSelectedData([...selectedData]);
	};

	const toggleSelectAll = () => {
		const any_data_selected = selectedData.length > 0;
		
		let new_selected_data = [];

		if(!any_data_selected) 
		{
			new_selected_data = displayResults.reduce((results, classroom) => {
				results.push(classroom.classroom_id);
				return results;
			}, []);
		}

		setSelectedData(new_selected_data);
	};
	
	const handeDeleteData = async (data_ids) => {
		const data = {school_id:schoolInfo.school_id, schedule_version_id:schoolInfo.current_schedule_version_id, data_type:'classroom', data_ids:data_ids};
		let delete_data_bool = true;
		
		if(!dontShowDeletePopup)
		{
			const options =  {
				title: "Are you sure?",
				icon: "warning",
				dangerMode: true,
				buttons: {
					cancel: {
						text: "Cancel",
						value: false,
						visible: true,
						className: 'gray-btn'
					},
					confirm: {
						text: "Yes",
						value: true,
						visible: true,
						className: 'red-btn'
					},
				},
				content: (
					<div>
						<div>
							<p>Do you really want to delete the following classrooms:</p>
							<div style={{marginLeft:'50px', textAlign:'left'}}>
								{data_ids.map((data_id, i) => {
									const data_index = databaseData.findIndex((classroom) => `${classroom.classroom_id}` === `${data_id}`);
									const data_info = databaseData[data_index];

									return (
										<div key={i}>{data_info.classroom_name}</div>
									);
								})}
							</div>
							<p className='red-text'>This will <strong>permanently</strong> remove them from the database.</p>
						</div>
						<div className='sweet-alert-dont-show-message' onClick={() => toggleDontShow('remove')}>
							<label className="checkmark_container">Please don&rsquo;t show me this message again
								<input type="checkbox" />
								<span className="checkmark"></span>
							</label>
						</div>
					</div>
				)
			};
		
			delete_data_bool = await swal(options);
		}
		
		if(delete_data_bool)
		{
			cancelCellEdit();
			const new_database_data = databaseData.filter((data_info) => !data_ids.some((data_id) => `${data_id}` === `${data_info.classroom_id}`));
			const new_filtered_data = filteredResults.filter((filtered_data) => !data_ids.some((data_id) => `${data_id}` === `${filtered_data.classroom_id}`));

			setDatabaseData(new_database_data);
			setFilteredResults(new_filtered_data);
			
			deleteData(data);

			setSelectedData([]);
		}
	};
	
	useEffect(() => {
		if(!showEditDataModal) setCurrentDataID(null);
		const search_bar = document.getElementsByClassName("school-data-search-bar")[0];
		const search_value = search_bar ? search_bar.value : '';
		if(search_value.length > 0) handleChange();
	}, [showEditDataModal, handleChange]);
	
	useEffect(() => {
		handleChange();
	}, [filteredResults, handleChange]);

	useEffect(() => {
		if(dataIsLoading || filterActive) return;

		filteredResultsRef.current = Array.isArray(visibleDatabaseData) ? visibleDatabaseData : [];
		setFilteredResults([...filteredResultsRef.current]);
		setHandleChangeComplete(false);
	}, [visibleDatabaseData, dataIsLoading, filterActive]);
	
	const row_style = {gridTemplateColumns:'35px 2fr 2fr 1fr 70px'};
	
	return (
		<>
		{showEditDataModal &&
			<EditClassroomData schoolInfo={schoolInfo} dataID={currentDataID} toggleEditData={toggleEditData} scheduleStructureData={scheduleStructureData} scheduleInfo={scheduleInfo} classrooms={databaseData} courses={courses} teachers={teachers} sections={sections} departments={departments} setDatabaseData={setDatabaseData} filteredResults={filteredResults} setFilteredResults={setFilteredResults}/>
		}
		{showUploadModal && !readOnlyAddUpload &&
			<UploadModal schoolInfo={schoolInfo} scheduleInfo={scheduleInfo} uploadTitle='Classrooms' uploadAPIName='classroomdata' closeModal={() => setShowUploadModal(false)} handlePullNewData={handlePullNewData}/>
		}
		<div className='school-data-content-screen'>
			<h1 className='school-data-main-heading capitalize'>Classrooms</h1>
			<div className='school-data-top-bar'>
				<div className='school-data-search-filter-row'>
					<div className='school-data-search-bar-container'>
						<input className='school-data-search-bar' placeholder="Search for classrooms..." value={searchValue} onChange={(e) => setSearchValue(e.target.value)} onKeyDown={(e) => handleIsSearching(e)} />
						<FontAwesomeIcon className='school-data-search-bar-icon' icon={faSearch}/>
					</div>
					<SchoolDataFilter categories={schoolDataFilterCategories} onChange={handleFilterClick} resultCount={displayResults.length}/>
				</div>
				{!readOnlyAddUpload && <div className='btn btn-icon-left upload-action-btn' onClick={() => {
					cancelCellEdit();
					setShowUploadModal(true);
				}}>
					<FontAwesomeIcon icon={faUpload} />
					<div>Upload</div>
				</div>}
			</div>
			<div className='school-data-database-display'>
				<div className='school-data-database-row school-data-database-display-header fixed-heading-on-scroll' style={row_style}>
					<div></div>
					<div className='school-data-database-header-col'>Classroom Name <SortToggle handleOnClick={handleSortClick} sortKey='classroom_name' passedData={displayResults}/></div>
					<div className='school-data-database-header-col'>Department(s) <SortToggle handleOnClick={handleSortClick} sortKey='departments' passedData={displayResults}/></div>
					<div className='school-data-database-header-col'>Classroom Type <SortToggle handleOnClick={handleSortClick} sortKey='classroom_type' passedData={displayResults}/></div>
					{!readOnlyAddUpload ? <div className='school-data-database-add-new-container' onClick={() => toggleEditData(null)}>
						<div className='school-data-database-add-new-btn'>
							<FontAwesomeIcon className='white-text' icon={faPlus}/>
						</div>
					</div> : <div></div>}
				</div>
				{displayResults.length > 0 &&
					<div className='select-all-row'>
						<div className={`select-all-container ${selectedData.length > 0 && 'blue-text'}`} onClick={toggleSelectAll}>
							<FontAwesomeIcon icon={selectedData.length > 0 ? faMinusSquare : farSquare}/>
							<FontAwesomeIcon className='select-all-arrow' icon={faSortDown}/>
						</div>
						{selectedData.length > 0 &&
							<div className='small-text'>
								<div className='tooltip' data-tooltip-text='Delete classrooms' onClick={() => handeDeleteData(selectedData)}>
									<div className='red-link'>{`Delete ${selectedData.length} selected`}</div>
								</div>
							</div>
						}
					</div>
				}
				{(!handleChangeComplete && isLoading) ?
				(
					<div className='school-data-database-message-container'>
						<img src={require('../../../images/balls.gif')} alt='loading...' style={{height:'80px'}}/>
					</div>
				): isSearching ?
				(
					<div className='school-data-database-message-container'>
						<img src={require('../../../images/searching.gif')} alt='searching...' style={{height:'80px'}}/>
					</div>
				):
				(
					<>
					{displayResults.length === 0 &&
						<div className='school-data-database-message-container'>
							<h4>Sorry, no results were found!</h4>
						</div>
					}
					{Object.keys(scheduleStructureData).length > 0 &&
						<>
						{displayResults.map((data_info) => {
							const classroom_id = data_info.classroom_id;
							const data_selected = selectedData.some((selected_id) => `${selected_id}` === `${classroom_id}`);
							const editing_classroom_id = editingCell?.classroom_id;

							return (
								<div className='school-data-database-row' style={row_style} key={classroom_id}>
									<div className='school-data-row-checkbox-cell' onClick={() => toggleSelectData(classroom_id)}>
										<FontAwesomeIcon className={data_selected ? 'blue-text' : 'dark-gray-text'} icon={data_selected ? faCheckSquare : farSquare}/>
									</div>
									<div
										ref={isEditingField(classroom_id, 'classroom_name') ? activeEditorRef : null}
										className={isEditingField(classroom_id, 'classroom_name') ? 'school-data-inline-editor-container' : 'school-data-inline-editable-cell'}
										onClick={!editing_classroom_id ? () => startCellEdit(data_info, 'classroom_name') : undefined}
									>
										{isEditingField(classroom_id, 'classroom_name') ?
											(isSavingCell ?
												<div className='school-data-inline-saving-cell'>
													<img src={require('../../../images/balls.gif')} alt='saving...' className='school-data-inline-saving-gif'/>
												</div>
											:
												<input
													ref={classroomNameInputRef}
													className='school-data-inline-edit-input'
													value={cellDraft?.classroom_name || ''}
													disabled={isSavingCell}
													onChange={(e) => updateDraftField('classroom_name', e.target.value)}
													onKeyDown={(e) => handleCellKeyDown(e, data_info)}
													onBlur={() => handleCellBlur(data_info, 'classroom_name')}
												/>
											)
										:
											<div>{data_info.classroom_name}</div>
										}
									</div>
									<div
										ref={isEditingField(classroom_id, 'departments') ? activeEditorRef : null}
										className={isEditingField(classroom_id, 'departments') ? 'school-data-inline-editor-container' : 'school-data-inline-editable-cell'}
										onClick={!editing_classroom_id ? () => startCellEdit(data_info, 'departments') : undefined}
									>
										{isEditingField(classroom_id, 'departments') ?
											(isSavingCell ?
												<div className='school-data-inline-saving-cell school-data-inline-saving-cell-floating'>
													<img src={require('../../../images/balls.gif')} alt='saving...' className='school-data-inline-saving-gif'/>
												</div>
											:
												<div className='school-data-inline-multiselect-popover'>
													{departments.length === 0 ?
														<div className='medium-text'>No departments available.</div>
													:
														departments.map((department_info) => {
															const department_id = `${department_info.department_id}`;
															const department_selected = cellDraft?.departments?.includes(department_id);

															return (
																<label key={department_id} className='school-data-inline-multiselect-option'>
																	<input
																		type='checkbox'
																		checked={department_selected}
																		disabled={isSavingCell}
																		onChange={() => toggleDepartmentSelection(department_id)}
																	/>
																	<span>{department_info.department}</span>
																</label>
															);
														})
													}
												</div>
											)
										:
											<div>{renderDepartmentDisplay(data_info.departments)}</div>
										}
									</div>
									<div
										ref={isEditingField(classroom_id, 'classroom_type_id') ? activeEditorRef : null}
										className={isEditingField(classroom_id, 'classroom_type_id') ? 'school-data-inline-editor-container' : 'school-data-inline-editable-cell'}
										onClick={!editing_classroom_id ? () => startCellEdit(data_info, 'classroom_type_id') : undefined}
									>
										{isEditingField(classroom_id, 'classroom_type_id') ?
											(isSavingCell ?
												<div className='school-data-inline-saving-cell school-data-inline-saving-cell-floating'>
													<img src={require('../../../images/balls.gif')} alt='saving...' className='school-data-inline-saving-gif'/>
												</div>
											:
												<div className='school-data-inline-select-popover'>
													{classroomTypeOptions.length === 0 ?
														<div className='medium-text'>No classroom types available.</div>
													:
														<>
															<button type='button' className={`school-data-inline-select-option school-data-inline-select-option-none ${`${cellDraft?.classroom_type_id || ''}` === '' ? 'school-data-inline-select-option-selected' : ''}`} onClick={() => handleClassroomTypeSelect(data_info, '')}>
																<span className='school-data-inline-select-option-none-label'>None</span>
																<span className='school-data-inline-select-option-none-helper'>No classroom type</span>
															</button>
															{classroomTypeOptions.map((classroom_type_info) => {
																const classroom_type_id = `${classroom_type_info.classroom_type_id}`;
																const classroom_type_selected = `${cellDraft?.classroom_type_id || ''}` === classroom_type_id;

																return (
																	<button key={classroom_type_id} type='button' className={`school-data-inline-select-option ${classroom_type_selected ? 'school-data-inline-select-option-selected' : ''}`} onClick={() => handleClassroomTypeSelect(data_info, classroom_type_id)}>
																		{classroom_type_info.classroom_type}
																	</button>
																);
															})}
														</>
													}
												</div>
											)
										:
											<div className='medium-text'>{getClassroomTypeDisplay(data_info)}</div>
										}
									</div>
									<div className='school-data-database-row-btn-container'>
										<FontAwesomeIcon className='gray-to-dark-blue-link' icon={faEdit} onClick={() => toggleEditData(classroom_id)}/>
										<FontAwesomeIcon className='gray-to-red-link' icon={faTrashAlt} onClick={() => handeDeleteData([classroom_id])}/>
									</div>
								</div>
							);
						})}
						</>
					}
					</>
				)}
			</div>
		</div>
		</>
	);
}
